import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle } from "lucide-react"
import { useNavigate } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import { type LineaCotizacion, WizardCotizacion } from "./WizardCotizacion"

/**
 * El wizard de cotización con sus catálogos y su escritura.
 *
 * Separado del wizard por lo mismo que `DialogoPagoRubro` lo está de `ModalPago`: el
 * wizard no sabe nada de la API y por eso se puede probar montándolo con dos listas
 * sueltas. Aquí viven las consultas y la mutación.
 *
 * ⚠ **No se manda `tasa_cambio`.** El wizard cotiza en USD y `CotizacionCrear` la
 * declara opcional con defecto cero. Inventar aquí una tasa —la de hoy, pongamos—
 * fijaría el costo en bolívares de algo que todavía no se ha aprobado, y la tasa que
 * vale es la que se congela al abrir el expediente. Ni la de la cotización, ni la de
 * hoy.
 *
 * ⚠ **Al crear se navega al detalle.** Una cotización recién creada está en
 * `BORRADOR` y todavía hay que enviarla y aprobarla: devolver al operador a la
 * bandeja lo obligaría a buscar en una tabla paginada la fila que acaba de crear.
 */

type Cliente = {
  id: number
  razon_social: string
}

type Aduana = {
  id: number
  nombre: string
}

type CotizacionCreada = {
  id: number
}

export function DialogoNuevaCotizacion({ onCerrar }: { onCerrar: () => void }) {
  const clienteQuery = useQueryClient()
  const navegar = useNavigate()

  const clientes = useQuery({
    queryKey: ["clientes"],
    queryFn: () => api.get<Cliente[]>("/api/v1/clientes?activo=true"),
  })

  // Basta con estar autenticado: la lista de aduanas del país no es reservada.
  const aduanas = useQuery({
    queryKey: ["aduanas"],
    queryFn: () => api.get<Aduana[]>("/api/v1/aduanas?activo=true"),
  })

  const crear = useMutation({
    mutationFn: (datos: { clienteId: number; aduanaId: number; lineas: LineaCotizacion[] }) =>
      api.post<CotizacionCreada>("/api/v1/cotizaciones", {
        cliente_id: datos.clienteId,
        aduana_id: datos.aduanaId,
        items: datos.lineas.map((linea, i) => ({
          tipo: linea.tipo,
          concepto: linea.concepto,
          cantidad: linea.cantidad,
          precio_unitario: linea.precioUnitario,
          // El orden de la pantalla es el orden que se guarda. Sin esto el servidor
          // los recibe todos en cero y los devuelve en el orden que quiera la base.
          orden: i,
        })),
      }),
    onSuccess: (creada) => {
      clienteQuery.invalidateQueries({ queryKey: ["cotizaciones"] })
      onCerrar()
      navegar(`/cotizaciones/${creada.id}`)
    },
  })

  if (clientes.isPending || aduanas.isPending) {
    return (
      <p role="status" className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando catálogos…
      </p>
    )
  }

  if (clientes.isError || aduanas.isError) {
    // Sin cliente o sin aduana no hay cotización que crear. Ofrecer el wizard con
    // los desplegables vacíos dejaría teclear todas las líneas para nada.
    return (
      <p
        role="alert"
        className="flex items-center gap-2 text-sm"
        style={{ color: "var(--destructive)" }}
      >
        <AlertCircle size={16} aria-hidden={true} />
        No se pudieron cargar los catálogos de clientes y aduanas.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <WizardCotizacion
        clientes={clientes.data.map((c) => ({ id: c.id, razonSocial: c.razon_social }))}
        aduanas={aduanas.data.map((a) => ({ id: a.id, nombre: a.nombre }))}
        onConfirmar={(datos) => crear.mutate(datos)}
      />

      {/* El error no cierra el wizard: lo tecleado sigue ahí, como en los modales
          de dinero. Perder tres líneas por un 422 es perder el trabajo, no el tiempo. */}
      {crear.isError && (
        <p
          role="alert"
          className="flex items-center gap-2 rounded-[var(--radius-control)] border p-2 text-sm"
          style={{ borderColor: "var(--destructive)", color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} />
          {crear.error instanceof ErrorApi
            ? `${crear.error.codigo} · ${crear.error.message}`
            : "No se pudo crear la cotización."}
        </p>
      )}
    </div>
  )
}
